import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { FiCheck, FiX, FiDollarSign, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import api from '../../api';

export default function SuperCharges() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [action, setAction] = useState('');
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;

      const { data } = await api.get('/api/super/charge-requests', { params });
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Failed to load charge requests:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openActionModal = (request, actionType) => {
    setSelectedRequest(request);
    setAction(actionType);
    setNote('');
    setShowModal(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;
    setProcessing(true);

    try {
      if (action === 'approve') {
        await api.post(`/api/super/charge-requests/${selectedRequest._id}/approve`, { note });
        alert('충전이 승인되었습니다');
      } else {
        await api.post(`/api/super/charge-requests/${selectedRequest._id}/reject`, { note });
        alert('충전 요청이 거절되었습니다');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    } finally {
      setProcessing(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning"><FiClock /> 대기</span>;
      case 'approved':
        return <span className="badge badge-success"><FiCheckCircle /> 승인</span>;
      case 'rejected':
        return <span className="badge badge-danger"><FiXCircle /> 거절</span>;
      default:
        return null;
    }
  };

  // Stats
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const pendingAmount = requests.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="super-charges-page">
      <div className="page-header">
        <h2>충전 요청 관리</h2>
      </div>

      {/* Summary */}
      {statusFilter === 'pending' && pendingCount > 0 && (
        <div className="summary-banner">
          <FiDollarSign />
          <span>
            <strong>{pendingCount}건</strong>의 충전 요청이 대기 중입니다.
            (총 {formatNumber(pendingAmount)}원)
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">전체</option>
          <option value="pending">대기 중</option>
          <option value="approved">승인됨</option>
          <option value="rejected">거절됨</option>
        </select>
      </div>

      {/* Requests Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>요청일</th>
              <th>테넌트</th>
              <th>금액</th>
              <th>입금자명</th>
              <th>요청자</th>
              <th>상태</th>
              <th>처리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading">로딩 중...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan="7" className="empty">충전 요청이 없습니다</td></tr>
            ) : (
              requests.map((request) => (
                <tr key={request._id} className={request.status === 'pending' ? 'highlight-row' : ''}>
                  <td>{format(new Date(request.createdAt), 'MM/dd HH:mm')}</td>
                  <td>
                    <div className="tenant-info">
                      <span className="tenant-name">{request.tenantName}</span>
                      <code className="tenant-id">{request.tenantId}</code>
                    </div>
                  </td>
                  <td className="amount">{formatNumber(request.amount)}원</td>
                  <td>{request.depositorName}</td>
                  <td>{request.requestedBy?.name || '-'}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    {request.status === 'pending' ? (
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => openActionModal(request, 'approve')}
                        >
                          <FiCheck /> 승인
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => openActionModal(request, 'reject')}
                        >
                          <FiX /> 거절
                        </button>
                      </div>
                    ) : (
                      <div className="processed-info">
                        <small>
                          {request.processedBy?.name} ({format(new Date(request.processedAt), 'MM/dd')})
                        </small>
                        {request.processNote && (
                          <small className="note">{request.processNote}</small>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Action Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{action === 'approve' ? '충전 승인' : '충전 거절'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="request-summary">
                <div className="summary-row">
                  <span>테넌트:</span>
                  <strong>{selectedRequest.tenantName}</strong>
                </div>
                <div className="summary-row">
                  <span>요청 금액:</span>
                  <strong className="amount">{formatNumber(selectedRequest.amount)}원</strong>
                </div>
                <div className="summary-row">
                  <span>입금자명:</span>
                  <strong>{selectedRequest.depositorName}</strong>
                </div>
              </div>

              <div className="form-group">
                <label>메모 {action === 'reject' && '*'}</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={action === 'approve' ? '승인 메모 (선택)' : '거절 사유를 입력하세요'}
                  rows={3}
                  required={action === 'reject'}
                />
              </div>

              {action === 'approve' && (
                <div className="confirm-message success">
                  <FiCheckCircle />
                  <span>승인 시 해당 테넌트에 {formatNumber(selectedRequest.amount)}P가 충전됩니다.</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                취소
              </button>
              <button
                type="button"
                className={`btn ${action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleAction}
                disabled={processing || (action === 'reject' && !note)}
              >
                {processing ? '처리 중...' : (action === 'approve' ? '승인' : '거절')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
